import { useEffect } from 'react';

const STAGGER_MS = 100;

function applyReveal(el: HTMLElement) {
  if (el.classList.contains('is-visible')) return;
  const delay = el.dataset.revealDelay != null ? Number(el.dataset.revealDelay) : 0;
  el.style.transitionDelay = `${delay}ms`;
  el.classList.add('is-visible');
}

function groupItems(group: Element) {
  return [...group.querySelectorAll(':scope > .reveal')] as HTMLElement[];
}

function inView(el: Element | null) {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const rootBottom = vh - 40;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, rootBottom);
  const visible = Math.max(0, visibleBottom - visibleTop);
  if (visible <= 0) return false;
  return visible >= Math.min(rect.height * 0.12, 40);
}

/** Scroll-reveal for .reveal / [data-reveal-group] - same behavior as the old site.js */
export function useReveal(deps: unknown[] = []) {
  useEffect(() => {
    const reveals = [...document.querySelectorAll('.reveal')] as HTMLElement[];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      reveals.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!reveals.length) return;

    const groups = [...document.querySelectorAll('[data-reveal-group]')];
    const groupedEls = new Set<HTMLElement>();

    groups.forEach((group) => {
      const items = groupItems(group);
      const step = Number((group as HTMLElement).dataset.revealStep) || STAGGER_MS;
      items.forEach((el, i) => {
        if (el.dataset.revealDelay == null) el.dataset.revealDelay = String(i * step);
        groupedEls.add(el);
      });
    });

    reveals.forEach((el) => {
      if (el.dataset.revealDelay == null) el.dataset.revealDelay = '0';
    });

    let io: IntersectionObserver | null = null;

    const revealEl = (el: HTMLElement) => {
      applyReveal(el);
      if (io) io.unobserve(el);
    };

    const revealGroup = (group: Element) => {
      const g = group as HTMLElement;
      if (g.dataset.revealed === 'true') return;
      g.dataset.revealed = 'true';
      groupItems(group).forEach(revealEl);
      if (io) io.unobserve(group);
    };

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const target = entry.target as HTMLElement;
            if (target.hasAttribute('data-reveal-group')) {
              revealGroup(target);
              return;
            }
            const parentGroup = target.closest('[data-reveal-group]');
            if (parentGroup && groupedEls.has(target)) {
              revealGroup(parentGroup);
              return;
            }
            revealEl(target);
          });
        },
        { threshold: 0, rootMargin: '0px 0px -40px 0px' }
      );
      groups.forEach((group) => {
        io!.observe(group);
        groupItems(group).forEach((el) => io!.observe(el));
      });
      reveals.forEach((el) => {
        if (!groupedEls.has(el)) io!.observe(el);
      });
    }

    const flush = () => {
      groups.forEach((group) => {
        if ((group as HTMLElement).dataset.revealed === 'true') return;
        const first = groupItems(group)[0] || group;
        if (inView(first) || inView(group)) revealGroup(group);
      });
      reveals.forEach((el) => {
        if (groupedEls.has(el) || el.classList.contains('is-visible')) return;
        if (inView(el)) revealEl(el);
      });
    };

    window.addEventListener('scroll', flush, { passive: true });
    window.addEventListener('resize', flush);
    const raf = requestAnimationFrame(flush);

    return () => {
      window.removeEventListener('scroll', flush);
      window.removeEventListener('resize', flush);
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
    };
  }, deps);
}
